import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment/moment";

import {
     cxClearWinnersExport,
     cxGetWinnerFilterOptionsRequest,
     cxGetWinnersExportRequest,
     cxGetWinnersRequest,
} from "../../../../../_redux/_actions";
import { cxAlwaysInTouchConstants } from "../../../../../_redux/_constants/cxAlwaysInTouch.constants";
import { monthDisplayName } from "../../cxAwardMonth";

const ALL_OPTION = { id: null, name: "All" };

const ACTIVATION_OPTIONS = [
     { id: null, name: "All" },
     { id: true, name: "Activated" },
     { id: false, name: "Not activated" },
];

const INITIAL_FILTERS = {
     activation: ACTIVATION_OPTIONS[0],
     area: ALL_OPTION,
     dealer: ALL_OPTION,
     month: ALL_OPTION,
};

const INITIAL_PAGE = { skip: 0, take: 25 };

const asDate = (value) => (value ? moment(value).format("DD/MM/YYYY") : "");

// Kim's template, in her order and under her headings. The exported file drops straight into what
// she already sends Renault, so nothing here may be renamed or reordered without her template
// changing first.
const EXPORT_COLUMNS = [
     ["Area", (row) => row.area || ""],
     ["Dealer Code", (row) => row.dealerCode || ""],
     ["Dealer Name", (row) => row.dealerName || ""],
     ["Role", (row) => row.role || ""],
     ["First Name", (row) => row.firstName || ""],
     ["Last Name", (row) => row.lastName || ""],
     ["email", (row) => row.email || ""],
     ["Activated Y/N", (row) => (row.accountActivated ? "Y" : "N")],
     ["Activation Date", (row) => asDate(row.accountActivatedOn)],
     ["Last logged in", (row) => asDate(row.lastLoginOn)],
     ["Reward value", (row) => row.rewardValueGbp ?? ""],
     ["Reward paid date", (row) => asDate(row.rewardPaidOn)],
];

const CxActivationReportGrid = () => {
     const actionDispatch = useDispatch();

     const winnersPage = useSelector(
          (state) => state?.cxAlwaysInTouch?.winnerList,
     );
     const winnerFilterOptions = useSelector(
          (state) => state?.cxAlwaysInTouch?.winnerFilterOptions,
     );
     const winnersExport = useSelector(
          (state) => state?.cxAlwaysInTouch?.winnersExport,
     );

     const isLoading = useSelector((state) =>
          (state?.loading?.pendingRequests || []).includes(
               cxAlwaysInTouchConstants.CX_GET_WINNERS_REQUEST,
          ),
     );
     const isExporting = useSelector((state) =>
          (state?.loading?.pendingRequests || []).includes(
               cxAlwaysInTouchConstants.CX_GET_WINNERS_EXPORT_REQUEST,
          ),
     );

     const [filters, setFilters] = useState(INITIAL_FILTERS);
     const [search, setSearch] = useState("");
     const [page, setPage] = useState(INITIAL_PAGE);
     const [sort, setSort] = useState([]);
     const [optionSearch, setOptionSearch] = useState({
          dealerSearch: "",
          areaSearch: "",
     });

     useEffect(() => {
          const timer = setTimeout(() => {
               actionDispatch(
                    cxGetWinnerFilterOptionsRequest({
                         dealerSearch: optionSearch.dealerSearch || undefined,
                         areaSearch: optionSearch.areaSearch || undefined,
                    }),
               );
          }, 300);

          return () => clearTimeout(timer);
     }, [actionDispatch, optionSearch]);

     // The criteria the report is built from. Held in one place so the grid and the export are
     // always asked for exactly the same set of awards.
     const criteria = useMemo(
          () => ({
               accountActivated: filters.activation.id,
               areaId: filters.area.id,
               dealershipId: filters.dealer.id,
               month: filters.month.id || undefined,
               searchTerm: search.trim() ? search.trim() : undefined,
               sortBy: sort.length ? sort[0].field : undefined,
               sortDirection: sort.length ? sort[0].dir : undefined,
          }),
          [filters, search, sort],
     );

     useEffect(() => {
          const timer = setTimeout(() => {
               actionDispatch(
                    cxGetWinnersRequest({
                         ...criteria,
                         page: Math.floor(page.skip / page.take) + 1,
                         pageSize: page.take,
                    }),
               );
          }, 300);

          return () => clearTimeout(timer);
     }, [actionDispatch, criteria, page]);

     // The export call returns every matching award, so the file is written as soon as it lands and
     // the held copy is cleared. Without clearing it, changing a filter and exporting again would
     // write the previous result.
     useEffect(() => {
          const rows = winnersExport?.data || winnersExport;

          if (!Array.isArray(rows)) {
               return;
          }

          const sheetRows = rows.map((row) => {
               const line = {};

               EXPORT_COLUMNS.forEach(([heading, read]) => {
                    line[heading] = read(row);
               });

               return line;
          });

          const sheet = XLSX.utils.json_to_sheet(sheetRows, {
               header: EXPORT_COLUMNS.map(([heading]) => heading),
          });

          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, sheet, "Activation Report");
          XLSX.writeFile(
               workbook,
               `cx-always-in-touch-activation-report-${moment().format("YYYY-MM-DD")}.xlsx`,
          );

          actionDispatch(cxClearWinnersExport());
     }, [actionDispatch, winnersExport]);

     const areaOptions = useMemo(
          () => [ALL_OPTION, ...(winnerFilterOptions?.areas || [])],
          [winnerFilterOptions],
     );

     const dealerOptions = useMemo(
          () => [ALL_OPTION, ...(winnerFilterOptions?.dealers || [])],
          [winnerFilterOptions],
     );

     const monthOptions = useMemo(
          () => [
               ALL_OPTION,
               ...(winnerFilterOptions?.months || []).map((month) => ({
                    id: month,
                    name: monthDisplayName(month) || month,
               })),
          ],
          [winnerFilterOptions],
     );

     const rows = useMemo(() => {
          const list = winnersPage?.winners || [];

          return list.map((row) => ({
               ...row,
               activatedYesNo: row.accountActivated ? "Y" : "N",
               activationDate: asDate(row.accountActivatedOn),
               lastLoggedIn: asDate(row.lastLoginOn),
               rewardPaidDate: asDate(row.rewardPaidOn),
          }));
     }, [winnersPage]);

     const totalCount = winnersPage?.totalCount || 0;

     const onFilterChange = useCallback((field, value) => {
          setFilters((previous) => ({ ...previous, [field]: value }));
          setPage(INITIAL_PAGE);
     }, []);

     const onOptionSearchChange = useCallback((field, event) => {
          setOptionSearch((previous) => ({
               ...previous,
               [field]: event.filter?.value || "",
          }));
     }, []);

     const exportReport = useCallback(() => {
          actionDispatch(cxGetWinnersExportRequest(criteria));
     }, [actionDispatch, criteria]);

     return (
          <div className="sba-telerik-components-wr">
               <div
                    className="sba-chart-section-top-search"
                    style={{
                         display: "flex",
                         flexDirection: "row",
                         flexWrap: "wrap",
                         alignItems: "flex-end",
                         gap: "12px",
                         width: "100%",
                    }}
               >
                    <Input
                         style={{ width: "260px" }}
                         value={search}
                         onChange={(event) => {
                              setSearch(event.value);
                              setPage(INITIAL_PAGE);
                         }}
                         placeholder="Search name, email or dealer"
                    />
                    <DropDownList
                         style={{ width: "180px" }}
                         label="Activation"
                         data={ACTIVATION_OPTIONS}
                         textField="name"
                         dataItemKey="name"
                         value={filters.activation}
                         onChange={(event) =>
                              onFilterChange("activation", event.value)
                         }
                    />
                    <DropDownList
                         style={{ width: "150px" }}
                         label="Area"
                         data={areaOptions}
                         textField="name"
                         dataItemKey="name"
                         value={filters.area}
                         filterable={true}
                         onFilterChange={(event) =>
                              onOptionSearchChange("areaSearch", event)
                         }
                         onChange={(event) => onFilterChange("area", event.value)}
                    />
                    <DropDownList
                         style={{ width: "280px" }}
                         label="Dealer"
                         data={dealerOptions}
                         textField="name"
                         dataItemKey="name"
                         value={filters.dealer}
                         filterable={true}
                         onFilterChange={(event) =>
                              onOptionSearchChange("dealerSearch", event)
                         }
                         onChange={(event) =>
                              onFilterChange("dealer", event.value)
                         }
                    />
                    <DropDownList
                         style={{ width: "180px" }}
                         label="Month"
                         data={monthOptions}
                         textField="name"
                         dataItemKey="name"
                         value={filters.month}
                         onChange={(event) => onFilterChange("month", event.value)}
                    />
                    <Button
                         type="button"
                         themeColor="primary"
                         disabled={isExporting || !totalCount}
                         onClick={exportReport}
                    >
                         {isExporting ? "Preparing..." : "Export to Excel"}
                    </Button>
               </div>

               <div style={{ position: "relative" }}>
                    {isLoading && (
                         <div className="k-loading-mask" style={{ zIndex: 10 }}>
                              <span className="k-loading-text">Loading</span>
                              <div className="k-loading-image" />
                              <div className="k-loading-color" />
                         </div>
                    )}
                    <Grid
                         data={rows}
                         skip={page.skip}
                         take={page.take}
                         total={totalCount}
                         sort={sort}
                         sortable={true}
                         fixedScroll={true}
                         className="sba-area-data-grid"
                         pageable={{ buttonCount: 4, pageSizes: [25, 50, 100] }}
                         onPageChange={(event) => setPage(event.page)}
                         onSortChange={(event) => {
                              setSort(event.sort);
                              setPage(INITIAL_PAGE);
                         }}
                    >
                         <GridColumn field="area" title="Area" width="110px" />
                         <GridColumn
                              field="dealerCode"
                              title="Dealer Code"
                              width="140px"
                         />
                         <GridColumn
                              field="dealerName"
                              title="Dealer Name"
                              width="240px"
                         />
                         <GridColumn field="role" title="Role" width="170px" />
                         <GridColumn
                              field="firstName"
                              title="First Name"
                              width="140px"
                         />
                         <GridColumn
                              field="lastName"
                              title="Last Name"
                              width="140px"
                         />
                         <GridColumn field="email" title="email" width="250px" />
                         <GridColumn
                              field="activatedYesNo"
                              title="Activated Y/N"
                              width="140px"
                              sortable={false}
                         />
                         <GridColumn
                              field="activationDate"
                              title="Activation Date"
                              width="160px"
                              sortable={false}
                         />
                         <GridColumn
                              field="lastLoggedIn"
                              title="Last logged in"
                              width="160px"
                              sortable={false}
                         />
                         <GridColumn
                              field="rewardValueGbp"
                              title="Reward value"
                              width="140px"
                              sortable={false}
                         />
                         <GridColumn
                              field="rewardPaidDate"
                              title="Reward paid date"
                              width="170px"
                              sortable={false}
                         />
                    </Grid>
               </div>
          </div>
     );
};

export default CxActivationReportGrid;
